<script setup>
  import { ref, computed, watch } from 'vue'
  import { useLocalePath } from '#i18n'
  import { useMainMenu } from '@/composables/useMainMenu'

  const isOpen = ref(false)
  const localePath = useLocalePath()
  const router = useRouter()
  const { menuMain, menuTop } = useMainMenu()
  const { getPictureUrl } = useMember()
  const { openLoginModal } = useAuthModal()
  const { openModal: openSearchModal } = useGlobalSearch()

  const authStore = useAuthStore()
  const user = computed(() => authStore.user)
  const { userData, isIndividuel, isCollectif, isFirme } = useAuthData()
  const { isPresident } = useIsPresident()
  const { hasUnpaidContribution } = useContributionStatus()

  // Enhanced animation state
  const isAnimating = ref(false)

  // Track which menu sections are expanded
  const expandedSections = ref({})
  const accountMenuExpanded = ref(false)

  // Initialize all sections as collapsed by default
  const initializeSections = () => {
    const sections = {}
    if (menuMain && typeof menuMain === 'object') {
      Object.keys(menuMain).forEach((key) => {
        sections[key] = false
      })
    }
    expandedSections.value = sections
    accountMenuExpanded.value = false
  }

  // Toggle section expand/collapse
  const toggleSection = (key) => {
    expandedSections.value = {
      ...expandedSections.value,
      [key]: !expandedSections.value[key]
    }
  }

  // Toggle account menu
  const toggleAccountMenu = () => {
    accountMenuExpanded.value = !accountMenuExpanded.value
  }

  // Check if a section is expanded
  const isSectionExpanded = (key) => {
    return expandedSections.value[key] === true
  }

  const toggleMenu = () => {
    isOpen.value = !isOpen.value

    if (isOpen.value) {
      document.body.style.overflow = 'hidden'
      initializeSections()
    } else {
      document.body.style.overflow = 'auto'
    }
  }

  const closeMenu = () => {
    isOpen.value = false
    document.body.style.overflow = 'auto'
  }

  const handleLoginClick = () => {
    openLoginModal()
    closeMenu()
  }

  const handleLogout = async () => {
    closeMenu()
    authStore.logout()
    await router.push(localePath({ name: 'index' }))
  }

  // Watch for route changes to auto-close menu
  watch(
    () => router.currentRoute.value.path,
    () => {
      if (isOpen.value) {
        closeMenu()
      }
    }
  )

  // Language selector
  const { locale, locales } = useI18n()
  const { changeLocale } = useLanguages()
  const languageMenuOpen = ref(false)

  const currentLanguage = computed(() => {
    return locales.value.find((loc) => loc.code === locale.value)
  })

  const toggleLanguageMenu = () => {
    languageMenuOpen.value = !languageMenuOpen.value
  }

  const selectLanguage = (localeCode) => {
    changeLocale(localeCode)
    languageMenuOpen.value = false
  }

  const accountMenuItems = computed(() => {
    const baseItems = [
      {
        to: { name: 'auth-private' },
        icon: 'tachometer-alt',
        label: 'pages.auth.private.dashboard.title'
      }
    ]

    // Individuel users get full menu
    if (isIndividuel.value) {
      return [
        ...baseItems,
        {
          to: { name: 'auth-private-profile' },
          icon: 'user',
          label: 'pages.auth.private.profile.title'
        },
        {
          to: { name: 'auth-private-events' },
          icon: 'calendar',
          label: 'pages.auth.private.events.title'
        },
        {
          to: { name: 'auth-private-my-documents' },
          icon: 'file-contract',
          label: 'pages.auth.private.myDocuments.title'
        },
        {
          to: { name: 'auth-private-commissions' },
          icon: 'users',
          label: 'pages.auth.private.commissions.title'
        },
        {
          to: { name: 'auth-private-committees' },
          icon: 'user-friends',
          label: 'pages.auth.private.committees.title'
        },
        {
          to: { name: 'auth-private-national-committees' },
          icon: 'globe',
          label: 'pages.auth.private.nationalCommittees.title'
        },
        ...(isPresident.value
          ? [
              {
                to: { name: 'auth-private-president-communication' },
                icon: 'envelope',
                label: 'pages.auth.private.presidentCommunication.title'
              }
            ]
          : []),
        // {
        //   to: { name: 'auth-private-mandates' },
        //   icon: 'scroll',
        //   label: 'pages.auth.private.mandates.title'
        // },
        {
          to: { name: 'auth-private-mandates' },
          icon: 'scroll',
          label: 'pages.auth.private.mandates.title'
        },
        {
          to: { name: 'auth-private-transactions' },
          icon: 'file-invoice',
          label: 'pages.auth.private.transactions.title'
        }
      ]
    }

    // Collectif (bar) users get bar-specific menu
    if (isCollectif.value) {
      return [
        ...baseItems,
        {
          to: { name: 'auth-private-bar-profile' },
          icon: 'building',
          label: 'pages.auth.private.barProfile.title'
        },
        {
          to: { name: 'auth-private-bar-members' },
          icon: 'users',
          label: 'pages.auth.private.barMembers.title'
        },
        ...(isPresident.value
          ? [
              {
                to: { name: 'auth-private-president-communication' },
                icon: 'envelope',
                label: 'pages.auth.private.presidentCommunication.title'
              }
            ]
          : []),
        {
          to: { name: 'auth-private-transactions' },
          icon: 'file-invoice',
          label: 'pages.auth.private.transactions.title'
        }
      ]
    }

    // Firme users get firm-specific menu
    if (isFirme.value) {
      return [
        ...baseItems,
        {
          to: { name: 'auth-private-firm-profile' },
          icon: 'building',
          label: 'pages.auth.private.firmProfile.title'
        },
        {
          to: { name: 'auth-private-firm-members' },
          icon: 'users',
          label: 'pages.auth.private.firmMembers.title'
        },
        ...(isPresident.value
          ? [
              {
                to: { name: 'auth-private-president-communication' },
                icon: 'envelope',
                label: 'pages.auth.private.presidentCommunication.title'
              }
            ]
          : []),
        {
          to: { name: 'auth-private-transactions' },
          icon: 'file-invoice',
          label: 'pages.auth.private.transactions.title'
        }
      ]
    }

    // Fallback to base items
    return baseItems
  })

  function handleSearchClick() {
    openSearchModal()
    closeMenu()
  }
</script>

<template>
  <div>
    <button
      class="group cursor-pointer relative flex items-center justify-center w-[35px] h-[35px] rounded-xl bg-gradient-to-br from-sky-900 via-sky-800 to-sky-950 hover:from-sky-800 hover:via-sky-700 hover:to-sky-900 shadow-lg hover:shadow-xl transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-sky-500/50 focus:ring-offset-2 overflow-hidden"
      :aria-expanded="isOpen"
      :aria-label="isOpen ? 'Close menu' : 'Open menu'"
      @click="toggleMenu"
    >
      <div
        class="absolute inset-0 bg-gradient-to-r from-sky-600/0 via-sky-400/20 to-sky-600/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
      ></div>
      <div class="relative w-5 h-5 z-10">
        <span
          class="absolute top-1/2 left-1/2 block w-[18px] h-[2px] bg-white rounded-full transition-all duration-300 ease-out -translate-x-1/2 shadow-sm"
          :class="{
            'rotate-45 -translate-y-1/2 bg-gradient-to-r from-white to-sky-100': isOpen,
            '-translate-y-[8px]': !isOpen
          }"
        ></span>
        <span
          class="absolute top-1/2 left-1/2 block w-[18px] h-[2px] bg-white rounded-full transition-all duration-300 ease-out -translate-x-1/2 -translate-y-1/2 shadow-sm"
          :class="{ 'opacity-0 scale-0': isOpen }"
        ></span>
        <span
          class="absolute top-1/2 left-1/2 block w-[18px] h-[2px] bg-white rounded-full transition-all duration-300 ease-out -translate-x-1/2 shadow-sm"
          :class="{
            '-rotate-45 -translate-y-1/2 bg-gradient-to-r from-white to-sky-100': isOpen,
            'translate-y-[6px]': !isOpen
          }"
        ></span>
      </div>
    </button>
    <Teleport to="body">
      <div
        class="fixed inset-0 z-[9999] transition-all duration-300 ease-out mt-[calc(var(--header-height))]"
        :class="{
          'opacity-100 visible': isOpen,
          'opacity-0 invisible pointer-events-none': !isOpen
        }"
      >
        <div
          class="absolute inset-0 bg-gradient-to-br from-slate-900/60 via-sky-900/40 to-slate-900/60 backdrop-blur-sm transition-all duration-300"
          @click="closeMenu"
        ></div>
        <div
          class="absolute right-0 top-0 h-[calc(100svh-var(--header-height))] w-full sm:w-[420px] bg-white shadow-2xl transform transition-transform duration-300 ease-out flex flex-col"
          :class="{
            'translate-x-0': isOpen,
            'translate-x-full': !isOpen
          }"
        >
          <div class="h-1 bg-gradient-to-r from-sky-500 via-sky-600 to-sky-700 shadow-lg flex-shrink-0"></div>
          <div class="p-6 flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-sky-600 scrollbar-track-slate-100">
            <nav class="space-y-6">
              <div v-if="!user" class="border-b border-slate-200 pb-6">
                <button
                  class="group cursor-pointer w-full relative overflow-hidden flex items-center justify-center gap-3 px-6 py-4 bg-gradient-to-r from-sky-600 via-sky-700 to-sky-800 hover:from-sky-700 hover:via-sky-800 hover:to-sky-900 text-white font-bold rounded-xl shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:scale-[1.02] active:scale-[0.98]"
                  @click="handleLoginClick"
                >
                  <div
                    class="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"
                  ></div>

                  <font-awesome
                    :icon="['fas', 'sign-in-alt']"
                    class="text-xl relative z-10 group-hover:rotate-12 transition-transform duration-300"
                  />
                  <span class="relative z-10 text-base tracking-wide">{{ $t('pages.auth.menu.connect') }}</span>
                </button>
              </div>
              <div v-if="user" class="border-b border-slate-200 pb-6">
                <div class="flex items-center justify-between mb-4">
                  <h3 class="text-xl font-bold text-slate-900 tracking-tight">
                    {{ $t('pages.auth.menu.myAccount') }}
                  </h3>
                  <div class="w-2 h-2 rounded-full bg-emerald-500 animate-pulse shadow-lg shadow-emerald-500/50"></div>
                </div>
                <div
                  class="mb-5 p-4 bg-gradient-to-br from-slate-50 via-sky-50 to-slate-50 rounded-xl border border-slate-200 shadow-md hover:shadow-xl transition-all duration-300"
                >
                  <div class="flex items-center gap-4">
                    <div class="relative flex-shrink-0">
                      <img
                        class="w-16 h-16 rounded-xl shadow-lg object-cover ring-4 ring-white transition-transform duration-300 hover:scale-105"
                        :src="getPictureUrl(userData)"
                        :alt="userData?.fullname || user.email"
                      />
                      <div
                        class="absolute -bottom-1 -right-1 w-5 h-5 bg-emerald-500 rounded-full border-2 border-white flex items-center justify-center"
                      >
                        <font-awesome class="text-white text-[10px]" :icon="['fas', 'check']" />
                      </div>
                    </div>
                    <div class="flex-1 min-w-0">
                      <ClientOnly>
                        <p class="font-bold text-slate-900 text-base truncate leading-tight mb-1">
                          {{ userData?.fullname || user.email }}
                        </p>
                        <p
                          v-if="userData?.user_code"
                          class="text-sky-700 text-sm font-semibold truncate flex items-center gap-1.5"
                        >
                          <font-awesome class="text-xs" :icon="['fas', 'id-badge']" />
                          {{ userData.user_code }}
                        </p>
                      </ClientOnly>
                    </div>
                  </div>
                </div>

                <!-- Contribution warning -->
                <NuxtLink
                  v-if="hasUnpaidContribution"
                  :to="localePath({ name: 'auth-private-profile' })"
                  class="flex items-start gap-2 mb-4 px-3 py-2.5 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-800 hover:bg-amber-100 transition-colors duration-200"
                  @click="closeMenu"
                >
                  <font-awesome :icon="['fas', 'exclamation-triangle']" class="text-amber-600 mt-0.5 flex-shrink-0" />
                  <span>{{ $t('pages.auth.contribution.warningTitle') }}</span>
                </NuxtLink>

                <button
                  type="button"
                  class="group w-full flex items-center justify-between p-3 rounded-xl hover:bg-slate-50 transition-all duration-300 cursor-pointer mb-3"
                  @click.prevent="toggleAccountMenu"
                >
                  <span
                    class="text-lg font-bold text-slate-900 tracking-tight group-hover:text-sky-700 transition-colors duration-300"
                  >
                    {{ $t('pages.auth.menu.myAccount') }}
                  </span>
                  <div
                    class="w-8 h-8 flex items-center justify-center rounded-lg bg-slate-100 group-hover:bg-sky-600 transition-all duration-300"
                  >
                    <font-awesome
                      :icon="['fas', 'chevron-down']"
                      class="text-sm text-slate-600 group-hover:text-white transition-all duration-300"
                      :class="{ 'rotate-180': accountMenuExpanded }"
                    />
                  </div>
                </button>
                <ClientOnly>
                  <Transition
                    enter-active-class="transition-all duration-300 ease-out"
                    enter-from-class="opacity-0 max-h-0"
                    enter-to-class="opacity-100 max-h-[2000px]"
                    leave-active-class="transition-all duration-200 ease-in"
                    leave-from-class="opacity-100 max-h-[2000px]"
                    leave-to-class="opacity-0 max-h-0"
                  >
                    <div v-show="accountMenuExpanded" class="overflow-hidden">
                      <TransitionGroup name="stagger-fade" tag="div" class="space-y-2">
                        <NuxtLink
                          v-for="(item, index) in accountMenuItems"
                          :key="item.label"
                          :to="localePath(item.to)"
                          class="group flex items-center gap-4 p-3.5 rounded-xl hover:bg-sky-50 transition-all duration-300 border border-transparent hover:border-sky-100"
                          :style="{ transitionDelay: `${index * 40}ms` }"
                          @click="closeMenu"
                        >
                          <div
                            class="w-10 h-10 flex items-center justify-center rounded-lg bg-sky-50 group-hover:bg-sky-100 transition-colors duration-300"
                          >
                            <font-awesome
                              :icon="['fas', item.icon]"
                              class="text-sky-600 group-hover:text-sky-700 transition-colors duration-300"
                            />
                          </div>
                          <span
                            class="text-slate-700 font-semibold group-hover:text-slate-900 transition-colors duration-300 flex-1"
                          >
                            {{ $t(item.label) }}
                          </span>
                          <font-awesome
                            :icon="['fas', 'chevron-right']"
                            class="text-slate-400 text-sm group-hover:text-sky-600 transition-colors duration-300"
                          />
                        </NuxtLink>
                      </TransitionGroup>
                    </div>
                  </Transition>
                </ClientOnly>
              </div>
              <div class="border-b border-slate-200 pb-6">
                <div class="relative">
                  <button
                    type="button"
                    class="w-full flex items-center justify-between p-3.5 rounded-xl bg-slate-50 hover:bg-sky-50 border border-slate-200 hover:border-sky-200 transition-all duration-300 cursor-pointer"
                    @click="toggleLanguageMenu"
                  >
                    <span class="text-slate-900 font-semibold capitalize">
                      {{ currentLanguage?.name }}
                    </span>
                    <font-awesome
                      :icon="['fas', 'chevron-down']"
                      class="text-slate-600 text-sm transition-transform duration-300"
                      :class="{ 'rotate-180': languageMenuOpen }"
                    />
                  </button>
                  <Transition
                    enter-active-class="transition-all duration-200 ease-out"
                    enter-from-class="opacity-0 -translate-y-2"
                    enter-to-class="opacity-100 translate-y-0"
                    leave-active-class="transition-all duration-150 ease-in"
                    leave-from-class="opacity-100 translate-y-0"
                    leave-to-class="opacity-0 -translate-y-2"
                  >
                    <div
                      v-show="languageMenuOpen"
                      class="mt-2 p-2 bg-white rounded-xl border border-slate-200 shadow-lg"
                    >
                      <button
                        v-for="loc in locales"
                        :key="loc.code"
                        type="button"
                        class="cursor-pointer w-full flex items-center gap-3 p-3 rounded-lg hover:bg-sky-50 transition-colors duration-200 text-left"
                        :class="{
                          'bg-sky-100 text-sky-900 font-semibold': loc.code === locale,
                          'text-slate-700': loc.code !== locale
                        }"
                        @click="selectLanguage(loc.code)"
                      >
                        <span class="capitalize">{{ loc.name }}</span>
                        <font-awesome
                          v-if="loc.code === locale"
                          :icon="['fas', 'check']"
                          class="text-sky-600 text-sm ml-auto"
                        />
                      </button>
                    </div>
                  </Transition>
                </div>
              </div>
              <!-- Global search button -->
              <div class="border-b border-slate-200 pb-6">
                <button
                  class="w-full flex items-center gap-3 p-3 rounded-xl bg-slate-50 hover:bg-sky-50 transition-all duration-200 cursor-pointer"
                  @click="handleSearchClick"
                >
                  <font-awesome class="text-sky-600" :icon="['fas', 'magnifying-glass']" />
                  <span class="text-sm font-medium text-slate-600">{{ $t('globalSearch.placeholder') }}</span>
                </button>
              </div>
              <template v-for="(section, key) in menuMain" :key="key">
                <div class="border-b border-slate-200 pb-6 last:border-0">
                  <button
                    type="button"
                    class="group w-full flex items-center justify-between p-3 rounded-xl hover:bg-slate-50 transition-all duration-300 cursor-pointer"
                    @click.prevent="toggleSection(key)"
                  >
                    <span
                      class="text-lg font-bold text-slate-900 tracking-tight group-hover:text-sky-700 transition-colors duration-300"
                    >
                      {{ section.title }}
                    </span>
                    <div
                      class="w-8 h-8 flex items-center justify-center rounded-lg bg-slate-100 group-hover:bg-sky-600 transition-all duration-300"
                    >
                      <font-awesome
                        :icon="['fas', 'chevron-down']"
                        class="text-sm text-slate-600 group-hover:text-white transition-all duration-300"
                        :class="{ 'rotate-180': isSectionExpanded(key) }"
                      />
                    </div>
                  </button>
                  <ClientOnly>
                    <Transition
                      enter-active-class="transition-all duration-300 ease-out"
                      enter-from-class="opacity-0 max-h-0"
                      enter-to-class="opacity-100 max-h-[2000px]"
                      leave-active-class="transition-all duration-200 ease-in"
                      leave-from-class="opacity-100 max-h-[2000px]"
                      leave-to-class="opacity-0 max-h-0"
                    >
                      <div v-show="isSectionExpanded(key)" class="overflow-hidden">
                        <TransitionGroup name="stagger-fade" tag="div" class="space-y-2 pt-3">
                          <NuxtLink
                            v-for="(item, index) in section.items"
                            :key="item.title"
                            :to="localePath({ name: item.path, params: item.params || {} })"
                            class="group flex items-center gap-3.5 p-3 pl-4 rounded-xl hover:bg-gradient-to-r hover:from-slate-50 hover:to-sky-50 transition-all duration-300 border border-transparent hover:border-sky-100 hover:shadow-sm"
                            :style="{ transitionDelay: `${index * 35}ms` }"
                            @click="closeMenu"
                          >
                            <div
                              class="w-9 h-9 flex items-center justify-center rounded-lg transition-all duration-300 group-hover:scale-110"
                            >
                              <font-awesome
                                :icon="['fas', item.icon]"
                                class="text-lg transition-colors duration-300"
                                :class="[item.iconCss.split(' ')[0], 'group-hover:text-sky-600']"
                              />
                            </div>
                            <span
                              class="text-slate-700 font-medium group-hover:text-slate-900 transition-colors duration-300 flex-1"
                            >
                              {{ item.title }}
                            </span>
                            <font-awesome
                              :icon="['fas', 'arrow-right']"
                              class="text-slate-300 text-xs opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 group-hover:text-sky-600 transition-all duration-300"
                            />
                          </NuxtLink>
                        </TransitionGroup>
                      </div>
                    </Transition>
                  </ClientOnly>
                </div>
              </template>
              <div class="border-b border-slate-200 pb-6">
                <div class="space-y-2">
                  <NuxtLink
                    v-for="page in menuTop.filter((p) => p.items[0].path !== 'index')"
                    :key="page.title"
                    :to="localePath({ name: page.items[0].path, params: page.items[0].params || {} })"
                    class="group flex items-center gap-3.5 p-3 rounded-xl hover:bg-gradient-to-r hover:from-slate-50 hover:to-sky-50 transition-all duration-300 border border-transparent hover:border-sky-100 hover:shadow-sm"
                    @click="closeMenu"
                  >
                    <div
                      class="w-9 h-9 flex items-center justify-center rounded-lg bg-sky-50 group-hover:bg-sky-100 transition-all duration-300 group-hover:scale-110"
                    >
                      <font-awesome
                        :icon="['fas', 'newspaper']"
                        class="text-lg text-sky-600 group-hover:text-sky-700 transition-colors duration-300"
                      />
                    </div>
                    <span
                      class="text-slate-700 font-medium group-hover:text-slate-900 transition-colors duration-300 flex-1"
                    >
                      {{ page.title }}
                    </span>
                    <font-awesome
                      :icon="['fas', 'arrow-right']"
                      class="text-slate-300 text-xs opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 group-hover:text-sky-600 transition-all duration-300"
                    />
                  </NuxtLink>
                </div>
              </div>
              <div class="pt-2 space-y-3">
                <NuxtLink
                  :to="localePath({ name: 'contact' })"
                  class="group cursor-pointer relative overflow-hidden w-full flex items-center justify-center gap-3 px-6 py-4 bg-gradient-to-r from-sky-600 via-sky-700 to-sky-800 hover:from-sky-700 hover:via-sky-800 hover:to-sky-900 text-white font-bold rounded-xl shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:scale-[1.02] active:scale-[0.98]"
                  @click="closeMenu"
                >
                  <div
                    class="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"
                  ></div>

                  <font-awesome
                    :icon="['fas', 'envelope']"
                    class="text-xl relative z-10 group-hover:rotate-12 transition-transform duration-300"
                  />
                  <span class="relative z-10 text-base tracking-wide">{{ $t('footer.contact') }}</span>
                </NuxtLink>
              </div>
              <div v-if="user" class="pt-2">
                <button
                  class="group cursor-pointer relative overflow-hidden w-full flex items-center justify-center gap-3 px-6 py-4 bg-gradient-to-r from-red-500 via-red-600 to-red-700 hover:from-red-600 hover:via-red-700 hover:to-red-800 text-white font-bold rounded-xl shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:scale-[1.02] active:scale-[0.98]"
                  @click="handleLogout"
                >
                  <div
                    class="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"
                  ></div>

                  <font-awesome
                    :icon="['fas', 'sign-out-alt']"
                    class="text-xl relative z-10 group-hover:-rotate-12 transition-transform duration-300"
                  />
                  <span class="relative z-10 text-base tracking-wide">{{ $t('pages.auth.menu.disconnect') }}</span>
                </button>
              </div>
            </nav>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<style scoped>
  /* Custom scrollbar styling */
  .scrollbar-thin::-webkit-scrollbar {
    width: 6px;
  }

  .scrollbar-thin::-webkit-scrollbar-track {
    background-color: rgb(241 245 249);
    border-radius: 9999px;
  }

  .scrollbar-thin::-webkit-scrollbar-thumb {
    background-color: rgb(2 132 199);
    border-radius: 9999px;
  }

  .scrollbar-thin::-webkit-scrollbar-thumb:hover {
    background-color: rgb(3 105 161);
  }

  /* Stagger fade animation for menu items */
  .stagger-fade-enter-active,
  .stagger-fade-leave-active {
    transition: all 0.3s ease-out;
  }

  .stagger-fade-enter-from {
    opacity: 0;
    transform: translateX(10px);
  }

  .stagger-fade-leave-to {
    opacity: 0;
    transform: translateX(-10px);
  }

  .stagger-fade-move {
    transition: transform 0.3s ease-out;
  }
</style>
